import './PROD_MULTI.scss';
import ProductBase from 'pages/PROD/ProductBase';

export default class PROD_MULTI extends ProductBase {

	onReady () {
		// Initialize Main-Image, Thumbnails, & PhotoSwipe Close-up Gallery
		this.initImageGallery();
		this.removeCloseupBackingElement();
		this.initAltStickyHeader();
		this.initSectionCollapse();
		this.initPurchaseEvents();
		this.initQuantify();
		this.initWishlists();
		this.initWaitlists();
		this.initTabcordion();
		this.initRangeMap();
		this.initQNA();
		this.initTooltips();
	}

	initSectionCollapse () {
		const sectionControlElements = document.getElementsByClassName('js-section-collapse-control');

		if (!sectionControlElements.length) {
			return;
		}

		for (const controlElement of sectionControlElements) {
			const controlTarget = controlElement.getAttribute('aria-controls');

			if (!controlTarget || !controlElement.hasAttribute('aria-expanded')) {
				continue;
			}

			const targetElement = document.getElementById(controlTarget);

			if (!targetElement) {
				continue;
			}

			controlElement.addEventListener('click', (event) => {
				event.preventDefault();

				if (controlElement.getAttribute('aria-expanded').toLowerCase() === 'true') {
					controlElement.setAttribute('aria-expanded', 'false');
					targetElement.classList.add('u-hidden');
				} else {
					controlElement.setAttribute('aria-expanded', 'true');
					targetElement.classList.remove('u-hidden');
				}
			});
		}
	}

	async initPurchaseEvents () {
		const addToCartElement = document.getElementById('js-add-to-cart');
		const purchaseFormElements = document.getElementsByClassName('js-purchase-form');

		if (!addToCartElement || !purchaseFormElements.length) {
			return;
		}

		const {AjaxMultiAddToCart} = await import('custom-extensions/ajax-multi-add-to-cart');

		new AjaxMultiAddToCart(Array.from(purchaseFormElements), {
			scrollToMessage: 'error',
			submitButtonElement: addToCartElement
		});

		for (const purchaseFormElement of purchaseFormElements) {
			purchaseFormElement.addEventListener('submit', (event) => {
				event.preventDefault();

				addToCartElement.dispatchEvent(new MouseEvent('click'));
			});
		}
	}

	async initQuantify () {
		const quantifyContainerElements = document.getElementsByClassName('js-product-purchase-quantify');

		if (!quantifyContainerElements[0]) {
			return;
		}

		const {Quantify} = await import('extensions/quantify');

		for (const quantifyContainerElement of quantifyContainerElements) {
			new Quantify({
				wrapper: quantifyContainerElement
			});
		}
	}

	// eslint-disable-next-line max-lines-per-function
	async initWishlists () {
		const addToWishlistFormElement = document.getElementById('js-add-to-wishlist-form');

		if (!addToWishlistFormElement) {
			return;
		}

		const wishlistDialogControlElements = document.getElementsByClassName('js-add-to-wishlist-control');

		if (!wishlistDialogControlElements.length) {
			return;
		}

		const wishlistDialogElement = document.getElementById(wishlistDialogControlElements[0].getAttribute('aria-controls'));

		if (!wishlistDialogElement) {
			return;
		}

		const wishlistFormElement = document.getElementById('js-create-wishlist-form');
		const wishlistDialogToggleEnabled = Boolean(wishlistFormElement);

		const {WishlistDialog} = await import('extensions/wishlists');

		for (const controlElement of wishlistDialogControlElements) {
			controlElement.wishlistDialog = new WishlistDialog(wishlistDialogElement, controlElement, {
				dialogScrollEnabled: false,
				showDialogParameter: '',
				toggleEnabled: wishlistDialogToggleEnabled
			});

			const controlFormElement = controlElement.closest('.js-wishlist-form');

			if (!controlFormElement) {
				continue;
			}

			const actionsContainerElement = controlElement.closest('.g-product-purchase-section__listing-item-actions');

			if (!actionsContainerElement) {
				continue;
			}

			controlFormElement.addEventListener('submit', (event) => {
				event.preventDefault();
			});

			controlElement.addEventListener('click', () => {
				let quantityValue = 1;

				const quantityInputElement = actionsContainerElement.querySelector('.js-purchase-form input[name="Quantity"]');

				if (quantityInputElement) {
					const quantityInputValue = quantityInputElement.value;

					if (!isNaN(quantityInputValue) && parseInt(quantityInputValue, 10) > 1) {
						quantityValue = quantityInputValue;
					}
				}

				const inputElements = controlFormElement.getElementsByTagName('input');
				let inputHtml = '';

				for (const inputElement of inputElements) {
					inputHtml += inputElement.outerHTML;
				}

				if (inputHtml !== '') {
					inputHtml += `<input type="hidden" name="Quantity" value="${quantityValue}">`;

					addToWishlistFormElement.innerHTML = inputHtml;
				}
			});
		}

		if (wishlistDialogToggleEnabled) {
			const {AjaxAddToWishlist, AjaxInsertWishlist} = await import('extensions/wishlists');
			const {default: EventBus} = await import('@mvps-genesis/event-bus');
			const {MessageContainer, Message} = await import('extensions/messages');

			// Wishlist dialog add to wishlist and insert wishlist functionality
			const messageElement = document.getElementById('js-add-to-cart-message');

			const messageContainer = new MessageContainer({
				container: messageElement
			});

			new AjaxInsertWishlist(wishlistFormElement);

			const ajaxAddToWishlist = new AjaxAddToWishlist(addToWishlistFormElement);
			const wishlistCountElements = document.getElementsByClassName('js-wishlists-count');

			/**
			 * Add new wishlist action element to action elements list on successful
			 * creation, and toggle display back to add to wishlist UI. Update elements
			 * containing wishlist counts with new wishlist count.
			 */
			EventBus.addEventListener('MVPS:insertWishlist', (event) => {
				const wishlist = event.detail?.data?.wishlist;

				if (!wishlist) {
					return;
				}

				const actionElement = ajaxAddToWishlist.createActionElement(wishlist);

				ajaxAddToWishlist.appendActionElement(actionElement);

				const autohideTime = ajaxAddToWishlist.messageOptions.success.options.autohideTime ?? 5000;

				ajaxAddToWishlist.createMessage(event.detail.message, {
					...ajaxAddToWishlist.messageOptions.success.options,
					autohide: true,
					autohideTime: autohideTime
				});

				setTimeout(() => {
					ajaxAddToWishlist.messageContainer.clear();
				}, autohideTime);

				const wishlistControlElement = document.querySelector('.js-add-to-wishlist-control[aria-expanded="true"]');

				if (wishlistControlElement) {
					wishlistControlElement.wishlistDialog?.toggle.elements.primary.controlElement.click();
				}

				actionElement.focus();

				if (wishlistCountElements) {
					let wishlistsCount = parseInt(wishlistCountElements[0].textContent, 10);

					if (!isNaN(wishlistsCount)) {
						wishlistsCount += 1;

						for (const wishlistCountElement of wishlistCountElements) {
							wishlistCountElement.textContent = wishlistsCount;
						}
					}
				}
			});

			/**
			 * Hide wishlist dialog on success and display success message in
			 * purchase form message element
			 */
			EventBus.addEventListener('MVPS:addToWishlist', (event) => {
				const addToWishlistSuccess = event.detail?.success;

				if (!addToWishlistSuccess || !messageContainer) {
					return;
				}

				messageContainer.add(new Message(event.detail.message, {
					...ajaxAddToWishlist.messageOptions.success.options,
					autohide: true
				}));

				const wishlistControlElement = document.querySelector('.js-add-to-wishlist-control[aria-expanded="true"]');

				if (wishlistControlElement) {
					wishlistControlElement.wishlistDialog?.controlElement.click();
				}

				ajaxAddToWishlist.messageContainer.clear();

				messageElement.scrollIntoView({
					behavior: 'smooth',
					block: 'center'
				});
			});
		}
	}

	// eslint-disable-next-line max-lines-per-function
	async initRangeMap () {
		const rangeMapElement = document.getElementById('panel-rangemap');

		if (!rangeMapElement) {
			return;
		}

		const {default: Modal} = await import('extensions/modal');
		const modal = new Modal();

		modal.init(['modal-range-map']);
	}

	// eslint-disable-next-line max-lines-per-function
	async initWaitlists () {
		const waitlistControlElements = document.getElementsByClassName('js-waitlist-control');

		if (!waitlistControlElements.length) {
			return;
		}

		const waitlistModalTriggerAttribute = 'data-waitlist-modal-trigger';
		const waitlistModalIds = [];

		for (const controlElement of waitlistControlElements) {
			const waitlistModalId = controlElement.getAttribute('data-waitlist-modal-trigger');

			if (!waitlistModalId) {
				continue;
			}

			waitlistModalIds.push(waitlistModalId);
		}

		if (!waitlistModalIds.length) {
			return;
		}

		const {default: Modal} = await import('extensions/modal');
		const modal = new Modal({triggerAttribute: waitlistModalTriggerAttribute});

		modal.init(waitlistModalIds);

		const waitlistFormClass = 'js-waitlist-form';
		const waitlistFormElements = document.getElementsByClassName(waitlistFormClass);

		if (!waitlistFormElements.length) {
			return;
		}

		const {HTMLFormValidator} = await import('extensions/form-validation');

		for (const waitlistFormElement of waitlistFormElements) {
			new HTMLFormValidator({
				errorInsertPosition: 'afterend',
				fieldContainer: `.${waitlistFormClass}`,
				formElement: waitlistFormElement
			});

			waitlistFormElement.addEventListener('submit', async (event) => {
				const containerElement = waitlistFormElement.closest('.js-waitlist-content');
				const resultsElement = containerElement?.querySelector('.js-waitlist-results');

				if (!resultsElement) {
					return;
				}

				event.preventDefault();

				const requestUrl = waitlistFormElement.action ?? this.pageContext.Json_Url;
				const formData = new FormData(waitlistFormElement);

				const response = await fetch(requestUrl, {
					body: formData,
					method: waitlistFormElement.method ?? 'POST'
				});

				if (!response.ok) {
					this.handleWaitlistAddError(resultsElement);
				}

				try {
					const responseBody = await response.json();

					if (!responseBody.success) {
						this.handleWaitlistAddError(resultsElement);
					}
				} catch {
					this.handleWaitlistAddError(resultsElement);
				}

				waitlistFormElement.classList.add('u-hidden');
				resultsElement.classList.remove('u-hidden');
			});
		}
	}

	handleWaitlistAddError (resultsElement) {
		const resultsIconElement = resultsElement.querySelector('.g-add-to-waitlist-dialog__results-icon');

		resultsIconElement?.classList.add('u-hidden');

		const resultsTextElement = resultsElement.querySelector('.g-add-to-waitlist-dialog__results-text');

		if (resultsTextElement) {
			resultsTextElement.innerHTML = 'There was an issue signing up for the waitlist. Please try again later.';
		}
	}

}
